/* Admin — Reader Review Queue panel (Feb-2026).
 *
 * Split-screen table:
 *   Left  — filterable list of review rows (open / closed / all).
 *   Right — detail card for the selected row with:
 *              • Assign-to-editor dropdown (admin action)
 *              • Approve / Request Changes / Reject buttons
 *                (any pro_writer admin can decide inline; scoped
 *                 editors use the dedicated `/editor/reviews/mine`
 *                 route surfaced by the same endpoint).
 *
 * Uses the same visual language as the other admin tables so it drops
 * into AdminPage without a fresh design pass.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Send, CheckCircle2, XCircle, AlertTriangle,
  User, Clock3, AlarmClock, Mail,
} from 'lucide-react';
import { openConfirm } from '@/components/admin/ConfirmDialog';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const STATUS_STYLES = {
  pending:           'text-amber-700 bg-amber-500/15',
  assigned:          'text-sky-700 bg-sky-500/15',
  approved:          'text-emerald-700 bg-emerald-500/15',
  changes_requested: 'text-orange-700 bg-orange-500/15',
  rejected:          'text-rose-700 bg-rose-500/15',
  cancelled:         'text-muted-foreground bg-secondary',
};

function hoursSince(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return (Date.now() - t) / 3600_000;
}

function SlaPill({ row, slaHours }) {
  const status = row?.status;
  if (status !== 'pending' && status !== 'assigned') return null;
  const hrs = hoursSince(row.submitted_at);
  if (hrs == null) return null;
  const escalated = row.escalated === true || (slaHours && hrs > slaHours);
  const tone = escalated
    ? 'bg-rose-500/15 text-rose-700 border-rose-500/40'
    : hrs > (slaHours || 24) * 0.66
      ? 'bg-amber-500/15 text-amber-800 border-amber-500/40'
      : 'bg-sky-500/15 text-sky-800 border-sky-500/40';
  return (
    <span data-testid={`rq-sla-${row.id}`}
          className={`ml-2 inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${tone}`}
          title={`${hrs.toFixed(1)}h since submit${slaHours ? ` · SLA ${slaHours}h` : ''}`}>
      <AlarmClock className="h-2.5 w-2.5" />
      {hrs < 1 ? `${Math.round(hrs * 60)}m` : `${hrs.toFixed(1)}h`}
      {escalated && ' · Overdue'}
    </span>
  );
}

function StatusPill({ status }) {
  return (
    <span data-testid={`rq-status-${status}`}
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_STYLES[status] || 'bg-secondary text-muted-foreground'}`}>
      {(status || '').replace(/_/g, ' ')}
    </span>
  );
}

export default function ReaderReviewQueuePanel() {
  const [rows,      setRows]      = useState([]);
  const [counts,    setCounts]    = useState({});
  const [filter,    setFilter]    = useState('open');
  const [editors,   setEditors]   = useState([]);
  const [selectedId, setSelected] = useState(null);
  const [busy,      setBusy]      = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [notes,     setNotes]     = useState('');
  const [pickedEditor, setPickedEditor] = useState('');
  const [slaHours,  setSlaHours]  = useState(24);
  const [tickBusy,  setTickBusy]  = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [q, e, sla] = await Promise.all([
        http.get(`/admin/reader-review/queue?status=${filter}`),
        http.get('/admin/reader-review/available-editors'),
        http.get('/admin/reader-review/sla-policy'),
      ]);
      setRows(q.data?.rows || []);
      setCounts(q.data?.counts || {});
      setEditors(e.data?.editors || []);
      setSlaHours(sla.data?.sla_hours || 24);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load queue.');
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { refresh(); }, [refresh]);

  const selected = useMemo(
    () => rows.find((r) => r.id === selectedId) || null,
    [rows, selectedId],
  );

  useEffect(() => {
    setNotes(selected?.decision_notes || '');
    setPickedEditor(selected?.assigned_to || '');
  }, [selectedId, selected]);

  const isOpen = selected && (selected.status === 'pending' || selected.status === 'assigned');

  const assign = async () => {
    if (!selected || !pickedEditor) return;
    setBusy(true);
    try {
      await http.post(`/admin/reader-review/${selected.id}/assign`,
                        { editor: pickedEditor });
      toast.success(`Assigned to ${pickedEditor}.`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Assign failed.');
    } finally { setBusy(false); }
  };

  const decide = async (decision) => {
    if (!selected) return;
    if (decision !== 'approve' && !(notes || '').trim()) {
      toast.error('Notes are required when requesting changes or rejecting.');
      return;
    }
    setBusy(true);
    try {
      const r = await http.post(`/editor/reviews/${selected.id}/decide`,
                                  { decision, notes });
      toast.success(`Decision recorded — ${r.data.status}`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Decide failed.');
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!selected) return;
    if (!(await openConfirm({ title: 'Cancel this pending review request?', tone: 'destructive' }))) return;
    setBusy(true);
    try {
      await http.post(`/admin/reader-review/${selected.id}/cancel`);
      toast.success('Cancelled.');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed.');
    } finally { setBusy(false); }
  };

  return (
    <section className="grid grid-cols-1 gap-4 lg:grid-cols-3"
             data-testid="admin-reader-review-queue">
      <BulkReassignCard editors={editors} onDone={refresh} />

      {/* ── list column ─────────────────────────────────────────── */}
      <div className="rounded-md border border-border bg-card p-4 lg:col-span-2">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="text-[13px] font-semibold">Reader review queue</div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              {rows.length} row{rows.length === 1 ? '' : 's'} · pending {counts.pending || 0} · assigned {counts.assigned || 0}
              · approved {counts.approved || 0} · changes {counts.changes_requested || 0}
              · rejected {counts.rejected || 0}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {['open', 'closed', ''].map((f) => (
              <button key={f || 'all'}
                onClick={() => setFilter(f)}
                data-testid={`rq-filter-${f || 'all'}`}
                className={`rounded-full border px-2 py-0.5 text-[10.5px] uppercase tracking-wider ${
                  filter === f ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-border bg-background hover:bg-secondary'}`}>
                {f || 'all'}
              </button>
            ))}
            <button onClick={refresh}
                    data-testid="rq-refresh-btn"
                    className="ml-2 inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] hover:bg-secondary">
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              Refresh
            </button>
            <button onClick={async () => {
                      setTickBusy(true);
                      try {
                        const r = await http.post('/admin/reader-review/tick-sla');
                        toast.success(`SLA tick: ${r.data.escalated} escalated (SLA ${r.data.sla_hours}h)`);
                        await refresh();
                      } catch (e) { toast.error(e.response?.data?.detail || 'Tick failed.'); }
                      finally { setTickBusy(false); }
                    }}
                    disabled={tickBusy}
                    data-testid="rq-sla-tick-btn"
                    className="ml-1 inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] hover:bg-secondary disabled:opacity-50">
              {tickBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <AlarmClock className="h-3 w-3" />}
              SLA tick ({slaHours}h)
            </button>
            <button onClick={async () => {
                      setDigestBusy(true);
                      try {
                        const r = await http.post('/admin/reader-review/send-digest?dry_run=false');
                        toast.success(`Digest: sent ${r.data.sent}, empty ${r.data.empty}, skipped ${r.data.skipped}`);
                      } catch (e) { toast.error(e.response?.data?.detail || 'Digest failed.'); }
                      finally { setDigestBusy(false); }
                    }}
                    disabled={digestBusy}
                    data-testid="rq-digest-btn"
                    className="ml-1 inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] hover:bg-secondary disabled:opacity-50">
              {digestBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
              Send digest
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-md border border-border">
          <table className="min-w-full text-[12px]">
            <thead className="bg-panel-header/40 text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Book</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Assigned</th>
                <th className="px-3 py-2 text-left">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                  No rows in this bucket.
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id}
                    onClick={() => setSelected(r.id)}
                    data-testid={`rq-row-${r.id}`}
                    className={`cursor-pointer border-t border-border transition ${
                      selectedId === r.id ? 'bg-primary/10' : 'hover:bg-secondary/60'}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium">
                      {r.book_title || r.book_id}
                      <SlaPill row={r} slaHours={slaHours} />
                    </div>
                    <div className="text-[10.5px] text-muted-foreground">
                      {r.sample_chapter_count} sample ch{r.force ? ' · force' : ''}
                    </div>
                  </td>
                  <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {r.assigned_to || '—'}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {(r.submitted_at || '').slice(0, 10)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── detail column ────────────────────────────────────────── */}
      <div className="rounded-md border border-border bg-card p-4"
           data-testid="rq-detail-column">
        {!selected && (
          <div className="text-[12px] text-muted-foreground">
            Select a row to view its detail and take action.
          </div>
        )}
        {selected && (
          <>
            <div className="mb-2 flex items-start justify-between gap-2">
              <div>
                <div className="text-[13px] font-semibold">{selected.book_title || selected.book_id}</div>
                <div className="text-[10.5px] text-muted-foreground">{selected.book_id}</div>
              </div>
              <StatusPill status={selected.status} />
            </div>
            <div className="space-y-1 text-[11.5px] text-muted-foreground">
              <div><Clock3 className="mr-1 inline h-3 w-3" />submitted {(selected.submitted_at || '').slice(0, 16).replace('T', ' ')} by <b className="text-foreground">{selected.submitted_by}</b></div>
              {selected.assigned_to && (
                <div><User className="mr-1 inline h-3 w-3" />assigned to <b className="text-foreground">{selected.assigned_to}</b></div>
              )}
              {selected.decided_by && (
                <div><CheckCircle2 className="mr-1 inline h-3 w-3" />decided by <b className="text-foreground">{selected.decided_by}</b> at {(selected.decided_at || '').slice(0, 16).replace('T', ' ')}</div>
              )}
              {selected.push_error && (
                <div className="mt-2 rounded border border-rose-500/30 bg-rose-500/10 p-1.5 text-rose-700">
                  Push error: {selected.push_error}
                </div>
              )}
            </div>

            {isOpen && (
              <div className="mt-3 rounded-md border border-border bg-secondary/30 p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider">Assign</div>
                <div className="mt-1.5 flex items-center gap-2">
                  <select value={pickedEditor}
                          onChange={(e) => setPickedEditor(e.target.value)}
                          data-testid="rq-assign-select"
                          className="flex-1 rounded-md border border-border bg-background px-2 py-1 text-[12px]">
                    <option value="">— pick an editor —</option>
                    {editors.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                  <button onClick={assign}
                          disabled={!pickedEditor || busy}
                          data-testid="rq-assign-btn"
                          className="rounded-md bg-primary px-3 py-1 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                    Assign
                  </button>
                </div>
              </div>
            )}

            <div className="mt-3">
              <label className="mb-1 block text-[11px] uppercase tracking-wider">Editor notes</label>
              <textarea
                value={notes} rows={4}
                onChange={(e) => setNotes(e.target.value)}
                disabled={!isOpen || busy}
                data-testid="rq-notes"
                placeholder="Required for Request Changes / Reject."
                className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[12px]"
              />
            </div>

            {isOpen && (
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={() => decide('approve')}
                        disabled={busy}
                        data-testid="rq-approve-btn"
                        className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                  Approve &amp; push
                </button>
                <button onClick={() => decide('request_changes')}
                        disabled={busy || !notes.trim()}
                        data-testid="rq-changes-btn"
                        className="inline-flex items-center gap-1 rounded-md bg-orange-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-orange-700 disabled:opacity-50">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Request changes
                </button>
                <button onClick={() => decide('reject')}
                        disabled={busy || !notes.trim()}
                        data-testid="rq-reject-btn"
                        className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-rose-700 disabled:opacity-50">
                  <XCircle className="h-3.5 w-3.5" />
                  Reject
                </button>
                <button onClick={cancel}
                        disabled={busy}
                        data-testid="rq-cancel-btn"
                        className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-[12px] hover:bg-secondary disabled:opacity-50">
                  Cancel request
                </button>
              </div>
            )}

            {!isOpen && selected.push_result && (
              <div className="mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-2 text-[11.5px] text-emerald-800">
                Push {selected.push_result.skipped ? 'skipped — hash matched' : 'succeeded'}.{' '}
                {selected.push_result.reader_book_id
                  ? <>Reader book: <code className="font-mono-stat">{selected.push_result.reader_book_id}</code></>
                  : null}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}


/* ------------------------------------------------------------------
 * Bulk-reassign card — top strip of the queue panel.
 * ------------------------------------------------------------------ */
function BulkReassignCard({ editors, onDone }) {
  const [fromEditor, setFrom] = useState('');
  const [toEditor,   setTo]   = useState('');
  const [preview,    setPrev] = useState(null);
  const [busy,       setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!fromEditor) { setPrev(null); return; }
    (async () => {
      try {
        const r = await http.get(`/admin/reader-review/bulk-reassign/preview?from_editor=${encodeURIComponent(fromEditor)}`);
        if (alive) setPrev(r.data);
      } catch { if (alive) setPrev(null); }
    })();
    return () => { alive = false; };
  }, [fromEditor]);

  const run = async () => {
    if (!fromEditor || !toEditor || fromEditor === toEditor) {
      toast.error('Pick two different editors.');
      return;
    }
    if (!(await openConfirm({
      title: `Move every OPEN review from ${fromEditor} to ${toEditor}?`,
      confirmLabel: 'Reassign',
    }))) return;
    setBusy(true);
    try {
      const r = await http.post('/admin/reader-review/bulk-reassign',
                                  { from_editor: fromEditor, to_editor: toEditor });
      toast.success(`Moved ${r.data.moved} review${r.data.moved === 1 ? '' : 's'}.`);
      setFrom(''); setTo(''); setPrev(null);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk reassign failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-border bg-secondary/40 p-3 lg:col-span-3"
         data-testid="rq-bulk-reassign-card">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[180px]">
          <label className="block text-[11px] uppercase tracking-wider text-muted-foreground">From editor</label>
          <select value={fromEditor}
                  onChange={(e) => setFrom(e.target.value)}
                  data-testid="rq-bulk-from"
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-[12px]">
            <option value="">— pick —</option>
            {editors.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="block text-[11px] uppercase tracking-wider text-muted-foreground">To editor</label>
          <select value={toEditor}
                  onChange={(e) => setTo(e.target.value)}
                  data-testid="rq-bulk-to"
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-[12px]">
            <option value="">— pick —</option>
            {editors.filter((u) => u !== fromEditor).map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </div>
        <div className="min-w-[120px] text-[11px] text-muted-foreground">
          {preview ? (
            <><b className="text-foreground">{preview.open_count}</b> open review{preview.open_count === 1 ? '' : 's'} would move.</>
          ) : (
            <>Pick a source editor to preview.</>
          )}
        </div>
        <button
          type="button"
          onClick={run}
          disabled={busy || !fromEditor || !toEditor || fromEditor === toEditor || (preview && preview.open_count === 0)}
          data-testid="rq-bulk-run-btn"
          className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Bulk reassign
        </button>
      </div>
    </div>
  );
}
